import React, { useState } from 'react';
import { Drawer, Tabs } from 'antd';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import { SourcingMarketplaceSearchType, ViewType } from '@/shared/types/sourcing-marketplace';
import { BROWSE_MARKETPLACE_TABS } from '@/shared/constants/sourcing-marketplace';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import SourcingMarketplaceSearch from '../../Common/SourcingMarketplaceSearch';
import { AllCategoryMap, CatalogCategory, CatalogCompany } from '@/shared/types/catalog';
import Link from 'next/link';
import queryString from 'query-string';
import SourcingFilters from '../SourcingFilters';
import { useParams } from 'next/navigation';
import { PublicSourcingMarketplaceCategoryPage } from '@/shared/types/business-setting';
import './index.less';
import { isEmpty, upperFirst } from 'lodash-es';
import { useHistoryList } from '@/lib/context/HistoryListProvider';
import SourcingCategoryFilter from '../CategoryFilter';

interface IProps {
  isMobile: boolean;
  category: CategoryConfig;
  vertical: VerticalName;
  viewType: ViewType;
  categoryList: CatalogCategory[];
  searchCounts: { [key: string]: number };
  categoryConfig: { [key: string]: CategoryConfig[] };
  defaultPopularSuppliers?: CatalogCompany[];
  categoryPage?: PublicSourcingMarketplaceCategoryPage;

  // used for mobile filter drawer in SourcingMarketplaceSearch component
  allL1ToL2ToL3Map: AllCategoryMap;
  ignoreL3CategoryIds?: number[];
  L3IdToCountMap?: { [key: string]: number };
}

const SourcingHeader = ({
  isMobile,
  category,
  vertical,
  viewType,
  categoryList,
  searchCounts,
  categoryConfig,
  defaultPopularSuppliers,
  categoryPage,
  allL1ToL2ToL3Map,
  ignoreL3CategoryIds,
  L3IdToCountMap,
}: IProps) => {
  const [mobileFilterDrawerVisible, setMobileFilterDrawerVisible] = useState<boolean>(false);
  const { queryParams } = useQueryParams<{}>();
  const params: any = useParams();
  const { setSourcingBreadcrumbs } = useHistoryList();

  const searchKey = queryParams.get(ParamKey.SEARCH);

  const isServices = vertical === VerticalName.SERVICES;
  const isInActiveSearch = !isEmpty(searchKey);
  const categoryTitle = category.title;
  const isProductsOrSuppliersTab = [ViewType.CATALOG_PRODUCTS, ViewType.SUPPLIERS].includes(viewType);

  const verticalTag = (
    <Link
      className="vertical-tag"
      href={queryString.stringifyUrl({
        url: `/sourcing/browse/all/${viewType === ViewType.CATALOG_PRODUCTS ? 'products' : 'suppliers'}`,
        query: {
          [ParamKey.SEARCH]: searchKey,
        },
      })}
      onClick={() => {
        setSourcingBreadcrumbs([]);
      }}
    >
      <span>{categoryTitle}</span>
      <img
        src="https://static.pietrastudio.com/public/file_uploads/eb4ded272a286a89929d3ee89ee48e1e.png"
        width={8}
        height={8}
        alt="close"
        loading="lazy"
      />
    </Link>
  );

  const renderPageTitle = () => {
    if (isInActiveSearch) {
      return (
        <h1 className="search-results-header-text">
          {isMobile ? (
            <>Search results </>
          ) : (
            <>
              Search results for<span className="search-text">{upperFirst(searchKey)}</span>
            </>
          )}
          {vertical && <>in{verticalTag}</>}
        </h1>
      );
    }
  };

  const placeholderText = 'Search suppliers, products, and services';

  return (
    <>
      <div className="sourcing-header">
        <div className="header">{renderPageTitle()}</div>
        <div className="bottom">
          <Tabs
            className="tabs"
            defaultActiveKey={viewType}
            activeKey={viewType}
            items={BROWSE_MARKETPLACE_TABS(isServices, params?.category).map(
              (item: { key: ViewType; label: string; url: string }) => {
                return {
                  key: item.key,
                  label: (
                    <Link
                      href={queryString.stringifyUrl({
                        url: item.url,
                        ...(searchKey
                          ? {
                              query: {
                                [ParamKey.SEARCH]: searchKey,
                              },
                            }
                          : {}),
                      })}
                    >
                      {item.label}
                      <span> for {category?.title}</span>
                    </Link>
                  ),
                };
              },
            )}
          />
          {isProductsOrSuppliersTab && (
            <SourcingMarketplaceSearch
              searchType={SourcingMarketplaceSearchType.BROWSE_MARKETPLACE}
              topRankedSearchTerms={category?.topRankedSearchTerms ?? []}
              viewType={viewType}
              placeholderText={placeholderText}
              defaultPopularSuppliers={defaultPopularSuppliers}
              labelText={placeholderText + ' about ' + category?.title}
              ifShowMobileFilterIcon={true}
              onMobileFilterIconClick={() => setMobileFilterDrawerVisible(true)}
            />
          )}
        </div>
      </div>

      {isMobile && mobileFilterDrawerVisible && (
        <Drawer
          className="mobile-sourcing-filter-drawer"
          placement="right"
          width="auto"
          closable={true}
          onClose={() => setMobileFilterDrawerVisible(false)}
          open={mobileFilterDrawerVisible}
          forceRender={true}
        >
          <SourcingCategoryFilter
            L2Vertical={vertical}
            L2CategoryConfig={category}
            allL1ToL2ToL3Map={allL1ToL2ToL3Map}
            ignoreL3CategoryIds={ignoreL3CategoryIds}
            L3IdToCountMap={L3IdToCountMap}
            viewType={viewType}
            allEntryPointCategoryConfig={categoryConfig}
          />
          <SourcingFilters viewType={viewType} />
        </Drawer>
      )}
    </>
  );
};

export default SourcingHeader;
